import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { PublicShell } from "@/components/public/public-shell";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { publicSiteSettingsQuery } from "@/routes/_public/route";
import { toWhatsAppLink } from "@/lib/whatsapp-link";
import { ACTIVATION_FALLBACK, safeActivationDestination } from "@/lib/trauma/activation";

export const Route = createFileRoute("/activate")({
  head: () => ({
    meta: [
      { title: "تفعيل دوراتك — د. سارة النحاس" },
      { name: "description", content: "صفحة تفعيل الدخول إلى دوراتك." },
      { name: "robots", content: "noindex, nofollow, noarchive, nosnippet" },
      { property: "og:title", content: "تفعيل دوراتك — د. سارة النحاس" },
      { property: "og:description", content: "صفحة تفعيل الدخول إلى دوراتك." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: ActivatePageInShell,
});

const EXPIRED_WA_TEXT = "مرحبًا، انتهت صلاحية رابط الدخول إلى دوراتي. أرجو إعادة إرساله";

function readFragment(): { token: string; to: string } {
  const params = new URLSearchParams(window.location.hash.replace(/^#/, ""));
  return { token: params.get("t") ?? "", to: safeActivationDestination(params.get("to")) };
}

function ActivatePage() {
  const navigate = useNavigate();
  // Reading the fragment only — nothing is verified on load, so a link
  // preview or crawler that fetches this page cannot use up the token.
  const [frag, setFrag] = useState<{ token: string; to: string }>({ token: "", to: ACTIVATION_FALLBACK });
  const [busy, setBusy] = useState(false);
  const [expired, setExpired] = useState(false);
  useEffect(() => setFrag(readFragment()), []);
  const { data: settings } = useQuery(publicSiteSettingsQuery());

  const plural = frag.to === ACTIVATION_FALLBACK;

  async function onEnter() {
    setBusy(true);
    const { token, to } = readFragment();
    // The ONLY verifyOtp call on this page: runs on her tap, never on load.
    const { error } = token
      ? await supabase.auth.verifyOtp({ token_hash: token, type: "email" })
      : { error: new Error("missing") };
    if (!error) {
      window.history.replaceState(null, "", window.location.pathname);
      navigate({ to: to as any });
      return;
    }
    const { data } = await supabase.auth.getSession();
    if (data.session) {
      navigate({ to: to as any });
      return;
    }
    setExpired(true);
    setBusy(false);
  }
  // end onEnter

  const wa = toWhatsAppLink((settings as any)?.clinic_whatsapp, EXPIRED_WA_TEXT);

  return (
    <div dir="rtl" className="flex min-h-[70svh] items-center justify-center px-5 py-10 text-dark-brown">
      <div className="w-full max-w-md rounded-ds-xl bg-surface p-8 text-center shadow-sm">
        {expired ? (
          <>
            <h1 className="font-display text-2xl font-bold">انتهت صلاحية الرابط</h1>
            {wa && (
              <Button asChild size="lg" className="mt-6 w-full">
                <a href={wa} target="_blank" rel="noopener noreferrer">راسلنا على واتساب</a>
              </Button>
            )}
          </>
        ) : (
          <>
            <h1 className="font-display text-2xl font-bold">{plural ? "تم تفعيل دوراتك" : "تم تفعيل دورتك"}</h1>
            <Button size="lg" className="mt-6 min-h-12 w-full rounded-ds-pill" disabled={busy} onClick={onEnter}>
              الدخول إلى دوراتي
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

function ActivatePageInShell() {
  return (
    <PublicShell ground="bg-cream-200">
      <ActivatePage />
    </PublicShell>
  );
}
